"use client"

import { motion } from "framer-motion"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Check, X, Clock, AlertCircle } from "lucide-react"
import { createRoute } from "@tanstack/react-router"
import MainAppRoute from "@/rootRoutes/MainApp"

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
}

const approvalRequests = [
  {
    id: 1,
    type: "Offer Approval",
    job: "Senior Frontend Engineer",
    requestedBy: "Sarah Johnson",
    avatar: "/placeholder.svg?height=32&width=32",
    date: "2 hours ago",
    priority: "high",
    details: "$120,000 base + equity",
  },
  {
    id: 2,
    type: "Salary Increase",
    job: "Backend Engineer",
    requestedBy: "Mike Chen",
    avatar: "/placeholder.svg?height=32&width=32",
    date: "4 hours ago",
    priority: "medium",
    details: "Counter-offer response",
  },
  {
    id: 3,
    type: "Interview Panel",
    job: "DevOps Engineer",
    requestedBy: "Alex Rivera",
    avatar: "/placeholder.svg?height=32&width=32",
    date: "1 day ago",
    priority: "low",
    details: "Additional technical round",
  },
  {
    id: 4,
    type: "Job Posting",
    job: "Product Manager",
    requestedBy: "Emma Davis",
    avatar: "/placeholder.svg?height=32&width=32",
    date: "2 days ago",
    priority: "medium",
    details: "New role approval",
  },
]


const ApprovalsPage = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/ops/approvals",
  component: Approvals,
})
export default ApprovalsPage

function Approvals() {
  const handleApprove = (id: number) => {
    console.log(`Approved request ${id}`)
  }

  const handleReject = (id: number) => {
    console.log(`Rejected request ${id}`)
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "destructive"
      case "medium":
        return "default"
      case "low":
        return "secondary"
      default:
        return "secondary"
    }
  }

  return (
    <motion.div className="p-6 space-y-6" initial="initial" animate="animate" variants={fadeIn}>
      <div>
        <h1 className="text-3xl font-bold">Approvals</h1>
        <p className="text-muted-foreground">Review and approve pending requests</p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">4</div>
            <p className="text-xs text-muted-foreground">Awaiting your review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Priority</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">1</div>
            <p className="text-xs text-muted-foreground">Requires immediate attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved Today</CardTitle>
            <Check className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">3</div>
            <p className="text-xs text-muted-foreground">Completed approvals</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">2.5h</div>
            <p className="text-xs text-green-600">-30min from last week</p>
          </CardContent>
        </Card>
      </div>

      {/* Approvals Table */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>Pending Approvals</CardTitle>
          <CardDescription>Review and take action on pending requests</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Request</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Requested By</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {approvalRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{request.type}</p>
                      <p className="text-sm text-muted-foreground">{request.details}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{request.job}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={request.avatar || "/placeholder.svg"} />
                        <AvatarFallback>
                          {request.requestedBy
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{request.requestedBy}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{request.date}</TableCell>
                  <TableCell>
                    <Badge variant={getPriorityColor(request.priority)}>{request.priority}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button size="sm" onClick={() => handleApprove(request.id)} className="h-8">
                        <Check className="h-3 w-3 mr-1" />
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleReject(request.id)} className="h-8">
                        <X className="h-3 w-3 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </motion.div>
  )
}
